import { useWalletConnection, useWatchLocalStorage } from '@/hooks';
import { ContextCreator, apiClient, useApiQuery } from '@/libs';
import queryKeys from '@/utils/constant/queryKeys';
import { useQueryClient } from '@tanstack/react-query';
import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useUserContext } from './User';

const initialValue: Partial<{
  cycle: any;
  platform: any;
  user: any;
  isLoading: boolean;
  refetch: Function;
  setTimer: Function;
  hoursRemaining: RefObject<number>;
  flashoutHoursRemaining?: RefObject<number>;
}> = {};
export const [RewardProvider, useRewardData] = ContextCreator(initialValue);

export function RewardDataContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { data } = useUserContext();
  const { isConnected, address } = useWalletConnection();
  const watchedToken = useWatchLocalStorage('accessToken');
  const rawToken =
    watchedToken ||
    data?.token ||
    (typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null);

  const token: string | null =
    typeof rawToken === 'string' &&
    rawToken.trim() &&
    rawToken !== 'undefined' &&
    rawToken !== 'null'
      ? rawToken.trim()
      : rawToken &&
          typeof rawToken === 'object' &&
          typeof rawToken.accessToken === 'string' &&
          rawToken.accessToken.trim()
        ? rawToken.accessToken.trim()
        : null;

  const queryClient = useQueryClient();

  const hoursRemaining = useRef<number | null>(null);
  const flashoutHoursRemaining = useRef<number | null>(null);

  const isEnabled = Boolean(
    isConnected && address && !!token && data?.isActivePlan
  );

  const { data: rewardData, isLoading, refetch } = useApiQuery(
    [queryKeys.goldStream, token],
    () => {
      if (!token) {
        throw new Error('Access token is required to fetch gold stream data.');
      }
      return apiClient.getPlatformGoldStream({
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    {
      enabled: isEnabled,
      refetchInterval: isEnabled ? 5 * 60 * 1000 : false,
      retry: 1,
    }
  );

  const safeRefetch = useCallback(() => {
    if (!token || !isConnected || !isEnabled) {
      return Promise.resolve({ data: undefined });
    }
    return refetch();
  }, [token, isConnected, isEnabled, refetch]);

  const setTimer = useCallback(() => {
    if (!token || !isConnected || !isEnabled) return;

    safeRefetch().then(({ data }) => {
      const cycleEndTime = (data as any)?.data?.cycle?.endDate;
      hoursRemaining.current =
        cycleEndTime && Math.abs(cycleEndTime) / 3600 > 0
          ? Math.abs(cycleEndTime) / 3600
          : null;

      const flashOutDate = (data as any)?.data?.cycle?.flashOutDate;
      flashoutHoursRemaining.current =
        flashOutDate && Math.abs(flashOutDate) / 3600 > 0
          ? Math.abs(flashOutDate) / 3600
          : null;
    });
  }, [token, isConnected, isEnabled, safeRefetch]);

  useEffect(() => {
    if (!token || !isConnected) {
      hoursRemaining.current = null;
      flashoutHoursRemaining.current = null;
      queryClient.removeQueries({ queryKey: [queryKeys.goldStream] });
    }
  }, [token, isConnected, queryClient]);

  useEffect(() => {
    if (rewardData) {
      const cycleEndTime = (rewardData as any)?.data?.cycle?.endDate;
      if (cycleEndTime && Math.abs(cycleEndTime) / 3600 > 0) {
        hoursRemaining.current = Math.abs(cycleEndTime) / 3600;
      }
      const flashOutDate = (rewardData as any)?.data?.cycle?.flashOutDate;
      if (flashOutDate && Math.abs(flashOutDate) / 3600 > 0) {
        flashoutHoursRemaining.current = Math.abs(flashOutDate) / 3600;
      }
    }
  }, [rewardData]);

  return (
    <RewardProvider
      value={[
        {
          ...rewardData?.data,
          isLoading,
          refetch: safeRefetch,
          setTimer,
          flashoutHoursRemaining,
          hoursRemaining,
        },
      ]}
    >
      {children}
    </RewardProvider>
  );
}
